import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

import { useAppSelector } from "@store/hooks";

export enum AccountType {
  FREE = "FREE",
  SUBSCRIPTION = "SUBSCRIPTION",
  MUSIC_CREATOR = "MUSIC_CREATOR",
}

/** Credit pause (ToU §17.4), as the plan pill needs to describe it: nothing
    wrong / a credit is missing (subscription still live, whitelisting frozen) /
    the paid period lapsed during the pause and nothing has been charged since. */
export type PauseState = "none" | "credit_missing" | "awaiting";

interface AccountTypeContextValue {
  /** The plan the account is ON — drives account pages, plan pills, nav. */
  accountType: AccountType;
  /** What the account may currently DO — drives the catalogue, the players and
      the download modal. Same as `accountType` except while the account is
      awaiting a Reactivation Election, where it is FREE (see the provider). */
  entitlementType: AccountType;
  /** Whether whitelisting is paused, and which of the two pauses it is. The
      plan pill renders "Paused" instead of the plan name when this is set. */
  pauseState: PauseState;
  setAccountType: (type: AccountType) => void;
}

const AccountTypeContext = createContext<AccountTypeContextValue>({
  accountType: AccountType.FREE,
  entitlementType: AccountType.FREE,
  pauseState: "none",
  setAccountType: () => {},
});

export const useAccountType = () => useContext(AccountTypeContext);

export const isSubscription = (type: AccountType) => type === AccountType.SUBSCRIPTION;

// Map backend plan string to frontend AccountType. Account vocab is
// `free | subscription | music_creator | license`; tier vocab on tracks
// is a separate concept (`free | premium`). See
// `docu/flows/admin/claims-list.md` for the canonical list.
function planToAccountType(plan: string | undefined): AccountType {
  switch (plan) {
    case "subscription":
      return AccountType.SUBSCRIPTION;
    case "music_creator":
      return AccountType.MUSIC_CREATOR;
    default:
      return AccountType.FREE;
  }
}

// Persist the dev-switcher's choice across reloads. Without this, any
// `window.location.reload()` (e.g. after enabling auto-resolve) drops the
// React-only `devOverride` flag, the provider re-fetches `/users/my`, and
// since the token key here doesn't match the app's actual key
// (`accessToken`) the fetch silently fails and we fall back to FREE.
const DEV_OVERRIDE_KEY = "dev-account-type";
const isValidAccountType = (v: string | null): v is AccountType =>
  v === AccountType.FREE
  || v === AccountType.SUBSCRIPTION
  || v === AccountType.MUSIC_CREATOR;

const readDevOverride = (): AccountType | null => {
  if (typeof window === "undefined") return null;
  const v = window.localStorage.getItem(DEV_OVERRIDE_KEY);
  return isValidAccountType(v) ? v : null;
};

/** The dev switcher is scoped to the canonical QA account so any other user
    logged in on the dev box can't get accidentally locked onto a stale
    override. Must stay in sync with `DevSwitcherRail` and the dev gate in
    `AccountLicensesPage`. */
const DEV_OWNER_EMAIL = "hoyaw23479@seosnaps.com";

export const AccountTypeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Single source of truth = Redux. `state.user.user.plan` is kept fresh
  // by `my()` / `login.fulfilled` etc; by deriving from it, this context
  // re-renders the moment Redux updates (e.g. immediately after a
  // successful subscription signup that dispatches `my()`). The old
  // implementation did its own one-shot fetch on mount, so post-signup
  // the header / mega menu / claims gating stayed on FREE until full
  // page reload.
  const userSlice = useAppSelector((s: any) => s.user?.user);
  const userPlan: string | undefined = userSlice?.plan;
  const userEmail: string | undefined = userSlice?.email;
  // Credit pause whose paid period ran out (ToU §17.4). `plan` deliberately
  // stays "subscription" so the reactivation checkout has a plan to restore,
  // but nothing is being paid — so entitlements are FREE until the user makes
  // a Reactivation Election. Gating it here rather than in each consumer is
  // what makes the whole catalogue agree: premium downloads lock, and the
  // "Buy a Single YouTube Licence" option (free-accounts-only) comes back.
  // Decision 2026-07-25, user.
  const awaitingReactivation: boolean = !!userSlice?.whitelistingAwaitingReactivation;
  const pauseState: PauseState = userSlice?.whitelistingPauseState === "credit_missing"
    || userSlice?.whitelistingPauseState === "awaiting"
    ? userSlice.whitelistingPauseState
    : "none";

  // Dev-override state. Hoya can flip account types via the dev switcher,
  // which writes to localStorage and bumps this counter so we re-read.
  const [overrideTick, setOverrideTick] = useState(0);

  // overrideTick is included so dev-switcher writes (which set localStorage
  // outside React's awareness) trigger a re-read without remounting consumers.
  const accountType = useMemo<AccountType>(() => {
    const isDevOwner = userEmail === DEV_OWNER_EMAIL;
    const storedOverride = isDevOwner ? readDevOverride() : null;
    if (storedOverride) return storedOverride;
    return planToAccountType(userPlan);
  }, [userPlan, userEmail, overrideTick]);

  // What the account may currently DO, as opposed to what plan it is on. The
  // two only diverge while awaiting reactivation, and they must stay separate:
  // the account pages (claims, subscription, plan pill, nav) have to keep
  // showing the subscriber view — that is where the whole reactivation flow
  // lives — while the catalogue, players and download modal have to behave as
  // if the user were on Free.
  const entitlementType = awaitingReactivation ? AccountType.FREE : accountType;

  const setAccountType = (type: AccountType) => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(DEV_OVERRIDE_KEY, type);
    }
    setOverrideTick((n) => n + 1);
  };

  // Suppress unused-var lint for the legacy useEffect dep marker.
  useEffect(() => { /* noop — kept for symmetry with previous provider */ }, []);

  return (
    <AccountTypeContext.Provider value={{ accountType, entitlementType, pauseState, setAccountType }}>
      {children}
    </AccountTypeContext.Provider>
  );
};
